import { Product, Order } from "@/lib/types";
import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import React from "react";
import { db } from "@/firebase/config";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  getDocs,
  deleteField
} from "firebase/firestore";
import { useToast } from "@/components/ui/use-toast";

const LOCAL_PRODUCTS = "spicy-masala-products";

// Remove initialProducts fallback. Only use Firestore data.
// const initialProducts: Product[] = [ ... ]; // <-- REMOVE or comment out this block

interface ProductContextType {
  products: Product[];
  loading: boolean;
  addProduct: (product: Omit<Product, 'id' | 'status' | 'createdAt'>) => Promise<boolean>;
  updateProduct: (id: string, product: Omit<Product, 'id' | 'status'>) => Promise<void>;
  archiveProduct: (id: string) => Promise<void>;
  restoreProduct: (id: string) => Promise<void>;
  deleteProductPermanently: (id: string) => Promise<void>;
  orders: Order[];
  addOrder: (order: Omit<Order, 'id' | 'createdAt'>) => void;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

interface ProductProviderProps {
  children: ReactNode;
  realtime?: boolean;
}

export function ProductProvider({ children, realtime = false }: ProductProviderProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const productsCache = React.useRef<Product[] | null>(null);
  const cacheTimestamp = React.useRef<number>(0);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
  const [orders, setOrders] = useState<Order[]>([
    {
      id: 'order-1',
      userId: 'user-1',
      items: [
        { productId: 'masala-1', name: 'Pure Turmeric Powder', quantity: 2, price: 11.99 },
        { productId: 'masala-3', name: 'banana powder', quantity: 1, price: 9.95 }
      ],
      total: 33.93,
      status: 'pending',
      shippingAddress: {
        name: 'Rahul Sharma',
        street: '123 Main Street',
        city: 'Mumbai',
        state: 'Maharashtra',
        zip: '400001',
        country: 'India',
        phone: '9999999999',
        email: 'rahul@example.com'
      },
      paymentMethod: 'cod',
      paymentStatus: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      user: { name: 'Rahul Sharma', email: 'rahul@example.com' }
    }
  ]);
  const { toast } = useToast();

  useEffect(() => {
    if (realtime) {
      // Real-time updates for admin panel
      const q = query(collection(db, "products"), orderBy("createdAt", "desc"));
      try {
        const unsub = onSnapshot(q, (snapshot) => {
          setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product)));
          setLoading(false);
        }, (error) => {
          console.error('Error fetching products (realtime):', error);
          toast({
            title: "Error",
            description: "Failed to fetch products. Please refresh or check your connection.",
            variant: "destructive",
          });
          setLoading(false);
        });
        return () => unsub();
      } catch (error) {
        console.error('Error setting up realtime product listener:', error);
        toast({
          title: "Error",
          description: "Failed to set up product listener.",
          variant: "destructive",
        });
        setLoading(false);
      }
    } else {
      // One-time fetch for main website with caching
      const fetchProducts = async () => {
        // Check if we have valid cached data
        const now = Date.now();
        if (productsCache.current && (now - cacheTimestamp.current) < CACHE_DURATION) {
          console.log('Using cached products');
          setProducts(productsCache.current);
          setLoading(false);
          return;
        }

        setLoading(true);
        try {
          // Fetch only active products for better performance
          const q = query(
            collection(db, "products"),
            orderBy("createdAt", "desc")
          );
          const snapshot = await getDocs(q);
          const fetchedProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
          
          // Update cache
          productsCache.current = fetchedProducts;
          cacheTimestamp.current = Date.now();
          
          setProducts(fetchedProducts);
        } catch (error) {
          console.error('Error fetching products:', error);
          
          // Use cached data if available even if expired
          if (productsCache.current) {
            console.log('Using expired cache due to error');
            setProducts(productsCache.current);
          } else {
            toast({
              title: "Error",
              description: "Failed to fetch products. Please refresh or check your connection.",
              variant: "destructive",
            });
          }
        } finally {
          setLoading(false);
        }
      };
      fetchProducts();
    }
  }, [realtime]);

  const addProduct = async (productData: Omit<Product, 'id' | 'status' | 'createdAt'>) => {
    try {
      console.log('🔍 Adding product with data:', productData);
      const { title, description, price, image, gst, shippingFee, ...rest } = productData;

      // Validate required fields
      if (!title?.trim() || !description?.trim() || typeof price !== 'number' || !image?.trim()) {
        console.error('❌ Validation failed:', { title, description, price, image });
        throw new Error("Please fill in all required product fields.");
      }

      // Create product data object with only defined and non-empty fields
      const productDataToSave: Record<string, any> = {
        title: title.trim(),
        description: description.trim(),
        price,
        image: image.trim(),
        status: "active",
        createdAt: new Date().toISOString(),
      };

      // Handle GST and shipping fee fields
      if (typeof gst === 'number') {
        productDataToSave.gst = gst;
      }
      if (typeof shippingFee === 'number') {
        productDataToSave.shippingFee = shippingFee;
      }

      // Handle optional fields
      if (rest.badge?.trim()) {
        productDataToSave.badge = rest.badge.trim();
      }

      // Handle gram options
      if (rest.gramOptions && Array.isArray(rest.gramOptions) && rest.gramOptions.length > 0) {
        productDataToSave.gramOptions = rest.gramOptions;
      }

      // Add the document to Firestore
      console.log('🔍 Saving product data to Firestore:', productDataToSave);
      await addDoc(collection(db, "products"), productDataToSave);
      console.log('✅ Product successfully added to Firestore');

      toast({
        title: "Product Added",
        description: `${title} was added successfully.`,
        variant: "default",
      });
      return true;
    } catch (error: any) {
      console.error('Error adding product to Firestore:', error);
      
      // Provide more specific error messages
      let errorMessage = "Failed to add product. Please try again.";
      if (error?.code === 'permission-denied') {
        errorMessage = "Permission denied. Please check your authentication.";
      } else if (error?.code === 'unavailable') {
        errorMessage = "Service unavailable. Please check your internet connection.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  };

  const updateProduct = async (id: string, productData: Omit<Product, 'id' | 'status'>) => {
    try {
      console.log('🔍 Updating product with data:', productData);
      const updateData: any = { ...productData };
      const fieldsToDelete: Record<string, any> = {};

      // Handle badge field - if empty, mark for deletion
      if ('badge' in updateData && !updateData.badge) {
        console.log('📝 Removing empty badge field');
        delete updateData.badge;
        fieldsToDelete.badge = deleteField();
      }

      // Handle gramOptions - if undefined or empty array, mark for deletion
      if ('gramOptions' in updateData) {
        if (updateData.gramOptions === undefined || 
            (Array.isArray(updateData.gramOptions) && updateData.gramOptions.length === 0)) {
          console.log('📝 Removing undefined/empty gramOptions field');
          delete updateData.gramOptions;
          fieldsToDelete.gramOptions = deleteField();
        }
      }

      // Handle GST and shipping fee fields - ensure they are numbers or remove them
      if ('gst' in updateData && typeof updateData.gst !== 'number') {
        console.log('📝 Removing invalid gst field');
        delete updateData.gst;
      }
      if ('shippingFee' in updateData && typeof updateData.shippingFee !== 'number') {
        console.log('📝 Removing invalid shippingFee field');
        delete updateData.shippingFee;
      }

      // Remove any remaining undefined values from updateData
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          console.log(`📝 Removing undefined field: ${key}`);
          delete updateData[key];
        }
      });

      console.log('✅ Final update data:', updateData);
      console.log('✅ Fields to delete:', fieldsToDelete);

      // Perform the update with both regular updates and field deletions
      await updateDoc(doc(db, "products", id), {
        ...updateData,
        ...fieldsToDelete
      });
      
      console.log('✅ Product updated successfully');
      
      toast({
        title: "Product Updated",
        description: "Product was updated successfully.",
        variant: "default",
      });
    } catch (error: any) {
      console.error('❌ Error updating product:', error);
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. Please contact admin to update Firebase security rules to allow gramOptions field updates.');
      }
      throw error;
    }
  };

  const archiveProduct = async (id: string) => {
    await updateDoc(doc(db, "products", id), { status: "archived" });
  };

  const restoreProduct = async (id: string) => {
    await updateDoc(doc(db, "products", id), { status: "active" });
  };

  const deleteProductPermanently = async (id: string) => {
    await deleteDoc(doc(db, "products", id));
  };

  const addOrder = (orderData: Omit<Order, 'id' | 'createdAt'>) => {
    const newOrder: Order = {
      ...orderData,
      id: `order-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };
    setOrders(prev => [newOrder, ...prev]);
  };

  return (
    <ProductContext.Provider value={{ products, loading, addProduct, updateProduct, archiveProduct, restoreProduct, deleteProductPermanently, orders, addOrder }}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProducts() {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
}

export function useOrders() {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within a ProductProvider');
  }
  return { orders: context.orders, addOrder: context.addOrder };
} 